import { useActionData, useNavigation, useParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useSuspenseQueries } from "@tanstack/react-query";
import type { EntryPart } from "@repo/shared-types";
import { api } from "@/lib/api";
import { partLabel } from "@/lib/event-form";
import { loadSpaceContext, useSpaceContext } from "@/lib/events";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { divisionLabel, levelLabel } from "@/lib/results";
import { tenantPath } from "@/lib/tenants";
import { formatMoment } from "@/lib/times";
import { Feel, PartVerification, RxPlusBadge } from "@/components/result-fields";
import { Conversation } from "@/components/conversation";
import { HidePhoto, PhotoView } from "@/components/photos";
import { ErrorNote } from "@/components/form";
import { Avatar } from "@/components/avatar";
import { errorMessage } from "@/lib/session";
import { toast } from "@/lib/toast";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";
import { Card } from "@/components/ui/card";

/**
 * One athlete's result on an event, as the space it is shared into reads it
 * (7e-2): each part as its board shows it, and the result's own reactions and
 * thread. A board line's athlete links here.
 *
 * The api applies the board's rules: notes and feels only while the athlete
 * shares them, and a part still waiting for verification recorded for its
 * athlete alone — to anybody else it reads as not done. A result taken off
 * the board is a 404 here, its thread kept and hidden until it is shared
 * again.
 */

export async function resultLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const entryId = params.entry ?? "";
  await Promise.all([
    loadSpaceContext(request, slug),
    read(queries.event(slug, eventId)),
    read(queries.result(slug, eventId, entryId)),
    read(queries.resultComments(slug, eventId, entryId)),
  ]);
  return null;
}

/** Nothing once it has worked — a toast says so — or why not, which stays on the page. */
type ActionResult = { error?: string };

/** A moderator hiding the result's photo from the space, or putting it back (7g). */
export async function resultAction({ request, params }: ActionFunctionArgs): Promise<ActionResult> {
  const form = await request.formData();
  const results = api.space(params.slug ?? "").events.results;
  const [eventId, entryId] = [params.event ?? "", params.entry ?? ""];
  try {
    switch (String(form.get("intent") ?? "")) {
      case "hide-photo":
        await results.hidePhoto(eventId, entryId);
        toast("Photo hidden from the space. Put it back from here.");
        return {};
      case "unhide-photo":
        await results.unhidePhoto(eventId, entryId);
        toast("Photo put back.");
        return {};
      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

export default function ResultPage() {
  const { slug = "", event: eventId = "", entry: entryId = "" } = useParams();
  const { tenant, zone, canModerate } = useSpaceContext(slug);
  const [{ data: event }, { data: result }, { data: thread }] = useSuspenseQueries({
    queries: [
      queries.event(slug, eventId),
      queries.result(slug, eventId, entryId),
      queries.resultComments(slug, eventId, entryId),
    ],
  });
  const outcome = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const athlete = result.athlete.name ?? `@${result.athlete.username}`;
  const eventPath = `${tenantPath(tenant)}/events/${event.id}`;

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to={eventPath}>{event.title}</BackLink>
        <PageTitle of={[result.mine ? "Your result" : `${athlete}'s result`, event.title]} />
        <h1 className="flex items-center gap-3 page-title">
          <Avatar person={result.athlete} size="md" />
          {result.mine ? "Your result" : `${athlete}'s result`}
        </h1>
        <p className="text-muted-foreground">
          {[
            result.athlete.name ? `@${result.athlete.username}` : null,
            result.division ? divisionLabel(result.division) : null,
            result.level ? levelLabel(result.level) : null,
            `done ${formatMoment(result.performedAt, zone)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>

      {outcome?.error && <ErrorNote message={outcome.error} />}

      {result.photo && result.photo.status !== "removed" && (
        <section aria-label="Photo" className="space-y-2">
          <PhotoView photo={result.photo} alt={`${athlete}'s photo`} spaceName={tenant.name} />
          {canModerate && !result.mine && result.photo.status === "ready" && (
            <HidePhoto hidden={result.photo.hiddenAt !== null} busy={busy} />
          )}
        </section>
      )}

      <Card size="sm" className="py-0">
        <ul className="divide-y divide-border" aria-label="Parts">
          {result.parts.map((part, index) => (
            <li key={part.id} className="space-y-1 px-(--card-spacing) py-4">
              <p className="font-heading text-base font-medium">
                {partLabel(index)}. {part.title}
              </p>
              <p className="text-muted-foreground">{part.summary}</p>
              <SharedPart part={part} showLevel={!result.level} spaceName={tenant.name} />
            </li>
          ))}
        </ul>
      </Card>

      <Conversation
        key={result.entryId}
        target={{ slug: tenant.slug, eventId: event.id, entryId: result.entryId }}
        page={thread}
        reactions={result.reactions}
        writes
        closed={event.status === "published" ? null : "Comments and reactions open when the event is published."}
      />
    </section>
  );
}

/** One part of the result: its scores and badge, how it stands, and what its athlete says about it. */
function SharedPart({ part, showLevel, spaceName }: { part: EntryPart; showLevel: boolean; spaceName: string }) {
  if (!part.recorded) return <p className="pt-1 text-sm text-muted-foreground">— not done</p>;
  const [first, ...tiebreaks] = part.scores;
  return (
    <div className="pt-1 text-sm">
      <span className="font-heading text-lg font-semibold text-foreground tabular-nums">{first?.display}</span>
      {tiebreaks
        .filter((tiebreak) => tiebreak.display)
        .map((tiebreak, index) => (
          <span key={index} className="text-muted-foreground">
            {" "}
            · then {tiebreak.display}
          </span>
        ))}
      {showLevel && part.level && <span className="text-muted-foreground"> · {levelLabel(part.level)}</span>}
      {part.rxPlus && <RxPlusBadge />} <Feel feel={part.feel} />
      {part.note && <p className="mt-0.5 whitespace-pre-line text-xs text-muted-foreground">{part.note}</p>}
      <PartVerification part={part} spaceName={spaceName} />
    </div>
  );
}
